import * as React from "react";
import InstallationModel from "../../core/models/Installation";
import ArrowDropDownIcon from "@material-ui/icons/ArrowDropDown";
import ArrowDropUpIcon from "@material-ui/icons/ArrowDropUp";
import StarIcon from "@material-ui/icons/Star";
import StarBorderIcon from "@material-ui/icons/StarBorder";
import { IInstallation } from "./../../core/contracts/IInstallation";
import useAuth from "../../core/hooks/useAuth";
import {
  Theme,
  makeStyles,
  createStyles,
  Tooltip,
  Typography,
  IconButton,
  AccordionSummary,
  Accordion,
  AccordionDetails,
  useTheme,
  useMediaQuery,
} from "@material-ui/core";
import TruckIcon from "../../assets/icons/TruckIcon";
import { formatDateTime, getDateFromTimezonePtBrToGMT } from "../../helpers/datetime";
import TankIndicator from "../TankIndicator";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import ExpandLessIcon from "@material-ui/icons/ExpandLess";
import clsx from "clsx";
import SimpleLink from "../SimpleLink";
import TankModel from "../../core/models/Tank";
import { LevelThreshold } from "../../core/models/LevelThreshold";
import {
  calculateLevelPercentage,
  getLevelStatusString,
  getLevelColor,
  checkForLevelDataDelay,
  checkTripPlanned,
  checkTripInProgress,
  RenderInstallationTankWarningsTooltip,
  BuildDelayErrorTooltip,
  RenderTanksWarnings,
} from "../../helpers/installationTankHelpers";
import { formatValue, isArrayValid } from "../../helpers/utility";
import { levelStatus } from "../../core/models/LevelStatus";
import ErrorIcon from "@material-ui/icons/Error";
import PauseIcon from "@material-ui/icons/PauseCircleFilled";
import { useHubXValue } from "../../core/hooks/useHubHooks";
import { useHubx } from "../../HubX";
import {
  EVENT_HOME_FILTER_ORDER_CHANGE,
  EVENT_HOME_FILTER_ORDER_NAME_CHANGE,
  EVENT_HOME_PAGE_TABLE_COLUMNS_SELECTION_CHANGE,
} from "../../Events";
import { SessionCache } from "../../helpers/cache";
import IUserResponse from "../../core/contracts/IUserResponse";
import useConstructor from "../../core/hooks/useConstructor";
import { ON_SEE_AS_MODE } from "../../core/constants/tooltipHelpersText";
import InfoIcon from "@material-ui/icons/Help";
import { OrderOptions } from "../../core/contracts/APIInstallationFilter";
import Loader from "react-loader-spinner";

const useStyles = makeStyles((theme: Theme) => {
  const borderPallet = `0.4px solid ${theme.customPalette.border.lightgray}`;

  return createStyles({
    root: {
      "overflowX": "auto",
      "& .MuiAccordion-root.Mui-expanded": {
        margin: 0,
      },
    },

    gridRow: {
      "display": "grid",
      "gridTemplateColumns": "2fr 1.5fr 1fr 4fr 2fr 2fr 2fr 1.5fr 1fr 1fr 1fr 1fr 0.75fr 0.5fr",
      "textAlign": "center",
      "alignItems": "center",
      "columnGap": `${theme.spacing(1)}px`,
      "padding": theme.spacing(0, 1),
      "color": theme.palette.text.secondary,
      "minHeight": 42,

      // 1024px override
      [theme.breakpoints.down("md")]: {
        padding: 0,
        gridTemplateColumns: "2fr 1.5fr 1fr 4fr 2fr 2fr 2fr 1.5fr 1.6fr 1.6fr 0.95fr 0.5fr",
        fontSize: "0.7rem",
      },

      "&:not(.header)": {
        "&:not(.secondary)": {
          backgroundColor: theme.customPaper.background.cardBackgroundTable,
          border: borderPallet,
        },

        "& > *:not(.last)": {
          borderRight: borderPallet,
          height: "50%",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        },

        "&.secondary": {
          "&:not(last-child)": {
            borderBottom: borderPallet,
          },
          // override borderRight from no secondary theme
          "& > *:not(.last)": {
            borderRight: "transparent",
          },
        },
      },

      "&:is(.header)": {
        background: `${theme.customPaper.background.cardBackgroundLightContrast} !important`,
      },

      "& .date-wrapper": {
        "& > *:last-child": {
          marginLeft: theme.spacing(0.5),
        },
      },

      "& .MuiTypography-body1": {
        fontSize: "0.875rem",
        [theme.breakpoints.down("md")]: {
          fontSize: "0.8rem",
        },
      },

      "& .baseWidth": {
        minWidth: "10%",
      },

      "& .divPointer": {
        cursor: "pointer",
      },

      "& .label-flex-wrapper": {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-around",
        flexWrap: "wrap",
        flex: 1,
      },

      "& .bold": {
        "& > *": {
          fontWeight: "bolder",
        },
      },

      "& .opaque": {
        opacity: 0.5,
      },

      "& .levelIndicatorWrapper": {
        "padding": theme.spacing(0, 1),
        "& > span": {
          marginLeft: theme.spacing(2),
          fontWeight: "bold",
        },
        "&:not(.secondary) > span": {
          fontSize: "1.25em",
          color: theme.customPalette.text.boldgrey,
        },
      },

      "& .disable-icon-padding": {
        "& *": {
          padding: 0,
        },
      },

      "& .name": {
        fontSize: "0.7rem",
        maxHeight: 42,
        whiteSpace: "break-spaces",
        overflow: "hidden",
        textOverflow: "ellipsis",
      },

      "& .max-width": {
        maxWidth: 90,
      },
    },

    gridRowFavorites: {
      gridTemplateColumns: "1fr 2fr 1.5fr 1fr 4fr 2fr 2fr 2fr 1.5fr 1fr 1fr 1fr 1fr 0.75fr 0.5fr",
      [theme.breakpoints.down("md")]: {
        gridTemplateColumns:
          "1fr 2fr 1.5fr 1fr 4fr 2fr 2fr 2fr 1.5fr 1.6fr 1.6fr 1.6fr 0.95fr 0.5fr",
      },
    },

    accordion: {
      "& .MuiAccordionSummary-root": {
        padding: 0,
        minHeight: "unset",
      },

      "& .MuiAccordionSummary-content": {
        "margin": 0,

        "& > *": {
          width: "100%",
        },
      },
    },

    icon: {
      "& .MuiSvgIcon-root": {
        fontSize: "2.5rem",
        [theme.breakpoints.down("sm")]: {
          fontSize: "2rem",
        },
      },
    },

    tanksWrapper: {
      display: "flex",
      flexDirection: "column",
      flex: 1,
    },

    opaque: {
      color: "transparent",
    },

    hidden: {
      // display: "none !important",
      "color": "transparent",
      "&*": {
        color: "transparent",
      },
      "& *": {
        color: "transparent",
      },

      "& > *": {
        display: "none",
      },
    },
  });
});

export type HomeTableDataType = {
  id: string | number;
  name?: string;
  linkLabel?: string;
  link?: string;
  level?: number;
  status?: levelStatus;
  uom?: string;
  capacity?: number;
  thresholds: LevelThreshold;
  lastReading?: Date;
  plannedTrip?: boolean;
  tripInProgress?: boolean;
  numberOfThanks?: number;
  favorite?: boolean;
  Tanks?: TankModel[];
  vendor?: string;
  autonomy?: string;
  tank_pressure?: number;
  tank_pressure_uom?: string;
  windowStart?: string;
  windowEnd?: string;
};

type HomeTableRowProps = {
  favoriteGroup?: boolean;
  hasFavorites?: boolean;
  handleFavorite?: (id: string, favoriteOption: boolean) => void;
  data: HomeTableDataType;
  isTank?: boolean;
  onSeeAsMode: boolean;
};

export const HomeTableColumns = [
  "Nome Fantasia",
  "Nível",
  "Nível Instântaneo",
  "Capacidade Total",
  "Autonomia Estimada",
  "Status Nível",
  "Última Medição",
  "Viagem Planejada",
  "Viagem Em Rota",
  "Tanques",
  "Pressão",
] as const;

export type HomeTableColumnsType = typeof HomeTableColumns[number];

// Column templates for favorite rows/header when "Início/Fim da Janela" are added (non-free
// users on favorite rows). Applied as inline styles (see showAdditionalWindowColumns usages)
// instead of a combined CSS class, since this must win unconditionally over gridRowFavorites.
const FAVORITES_WINDOW_COLUMNS =
  "1fr 2fr 1.5fr 1fr 4fr 2fr 2fr 2fr 1.5fr 1fr 1fr 1fr 1fr 0.5fr minmax(150px, 1.5fr) minmax(150px, 1.5fr) 0.5fr";
const FAVORITES_WINDOW_COLUMNS_MD =
  "1fr 2fr 1.5fr 1fr 4fr 2fr 2fr 2fr 1.5fr 1.6fr 1.6fr 1.6fr 0.6fr minmax(130px, 1.5fr) minmax(130px, 1.5fr) 0.5fr";
const FAVORITES_WINDOW_MIN_WIDTH = 1350;
const FAVORITES_WINDOW_MIN_WIDTH_MD = 1200;

// FIXME - Melhorar o comportamento e otimizar componentes

function HomeTableRow({
  data,
  hasFavorites,
  favoriteGroup,
  handleFavorite,
  isTank = false,
  onSeeAsMode,
}: HomeTableRowProps) {
  const authProvider = useAuth();
  const [isExpanded, setExpanded] = React.useState(false);
  const [nowDate, setNowDate] = React.useState(new Date());
  const [isLoading, setIsLoading] = React.useState<Boolean>(false);

  const delay = checkForLevelDataDelay(data.lastReading);
  const isFreeUserRow = !isTank && authProvider.isFreeUser;

  function HomeTableRowMain() {
    const showColumns = useHubXValue(
      EVENT_HOME_PAGE_TABLE_COLUMNS_SELECTION_CHANGE,
      HomeTableColumns,
      true
    );
    const theme = useTheme();
    const isMdDown = useMediaQuery(theme.breakpoints.down("md"));

    const warnings = isTank
      ? RenderInstallationTankWarningsTooltip(
          {
            lastReadingDate:
              data.lastReading &&
              new Date(
                new Date(data.lastReading).getTime() +
                  new Date(data.lastReading).getTimezoneOffset() * 60000
              ),
            level: data.level,
            levelStatus: data.status,
            capacity: data.thresholds.over,
            veryLowLevel: data.thresholds.veryLow,
            lowLevel: data.thresholds.low,
          },
          nowDate
        )
      : delay && data.lastReading
      ? BuildDelayErrorTooltip({ start: data.lastReading, end: nowDate })
      : undefined;

    const isSingleTank = data.Tanks?.length === 1;

    const handleFavoriteBtn = async (
      installationId: string | number,
      favorite: boolean | undefined
    ) => {
      if (handleFavorite) {
        const favoriteOption = !favorite;
        setIsLoading(true);
        await handleFavorite(String(installationId), favoriteOption);
        setIsLoading(false);
      }
    };

    const tanksWarnings =
      data.Tanks && !isSingleTank ? RenderTanksWarnings(data.Tanks, nowDate) : undefined;

    const showFreeUserWindow = !isTank && authProvider.isFreeUser;
    const showAdditionalWindowColumns = !isTank && !authProvider.isFreeUser && favoriteGroup;

    const showName = showColumns.includes("Nome Fantasia");
    const showLevel = !showFreeUserWindow && showColumns.includes("Nível");
    const showPlannedIcon = showColumns.includes("Viagem Planejada");
    const showTravelIcon = showColumns.includes("Viagem Em Rota");
    const showTanks = !showFreeUserWindow && showColumns.includes("Tanques");

    const autonomyOutage = React.useMemo(() => {
      return formatValue(data?.autonomy ? Number(data?.autonomy) : 0);
    }, [data]);

    const hasOnePressure = React.useMemo(() => {
      return data?.Tanks?.some((x) => x.tank_pressure != null && x.tank_pressure_uom != null);
    }, [data?.Tanks]);

    const pressure = React.useMemo(() => {
      if (authProvider.hasHighLevel()) {
        if (data?.Tanks) {
          if (data?.Tanks?.length > 1) {
            if (hasOnePressure) {
              return (
                <Tooltip title="Essa instalação possui mais de um tanque, para visualizar informações sobre pressão, expanda a visão por tanques">
                  <InfoIcon fontSize="small" color="primary" />
                </Tooltip>
              );
            } else {
              return data.Tanks[0].tank_pressure_uom ? "- " + data.Tanks[0].tank_pressure_uom : "";
            }
          }
          return formatValue(data.Tanks[0].tank_pressure) + " " + data.Tanks[0].tank_pressure_uom;
        }
        if (data.tank_pressure_uom) {
          return formatValue(data.tank_pressure) + " " + data.tank_pressure_uom;
        }
        return 0;
      }
      return null;
    }, [data.Tanks]);

    return (
      <div
        className={clsx(
          isTank && "secondary",
          classes.gridRow,
          !isTank && hasFavorites && classes.gridRowFavorites
        )}
        style={
          showAdditionalWindowColumns
            ? {
                gridTemplateColumns: isMdDown
                  ? FAVORITES_WINDOW_COLUMNS_MD
                  : FAVORITES_WINDOW_COLUMNS,
                minWidth: isMdDown ? FAVORITES_WINDOW_MIN_WIDTH_MD : FAVORITES_WINDOW_MIN_WIDTH,
              }
            : undefined
        }
      >
        {!isTank && hasFavorites && (
          <div className="baseWidth">
            {onSeeAsMode ? (
              <Tooltip title={ON_SEE_AS_MODE}>
                {data.favorite ? <StarIcon /> : <StarBorderIcon />}
              </Tooltip>
            ) : isLoading ? (
              <div>
                <Loader type="TailSpin" color="#00AB66" height={15} width={15} />
              </div>
            ) : (
              <IconButton
                onClick={() => handleFavoriteBtn(data.id, data.favorite)}
                disabled={isLoading}
              >
                {data.favorite ? <StarIcon /> : <StarBorderIcon />}
              </IconButton>
            )}
          </div>
        )}
        <div className="baseWidth">
          {data.link && data.linkLabel && (
            <SimpleLink
              label={data?.linkLabel}
              href={data?.link}
              tooltip="clique aqui para ser direcionado à tela de detalhes desta instalação"
            />
          )}
        </div>
        <div className={clsx(!showName && classes.hidden)}>
          <Tooltip title={(data.vendor ? `Fabricante: ${data.vendor} ` : null) || ""}>
            <Typography className="name">{data.name}</Typography>
          </Tooltip>
        </div>
        <div className="max-width">
          <Typography>{data.id}</Typography>
        </div>
        <div
          className={clsx(
            isTank && "secondary",
            delay && "opaque",
            "levelIndicatorWrapper",
            !showLevel && classes.hidden
          )}
        >
          {showLevel && (
            <>
              {warnings ? (
                <Tooltip onOpen={() => setNowDate(new Date())} title={warnings}>
                  {delay ? (
                    <PauseIcon color="error" fontSize="small" />
                  ) : (
                    <ErrorIcon color="error" fontSize="small" />
                  )}
                </Tooltip>
              ) : (
                <ErrorIcon className={classes.opaque} fontSize="small" />
              )}
              <TankIndicator
                status={data.status}
                capacity={data.capacity}
                level={data.level}
                thresholds={data.thresholds}
              />
              <span>{calculateLevelPercentage(data.level, data.capacity)}%</span>
            </>
          )}
        </div>
        <div
          className={clsx(
            "baseWidth",
            !showFreeUserWindow && !showColumns.includes("Nível Instântaneo") && classes.hidden
          )}
        >
          {showFreeUserWindow ? (
            <Typography>{formatDateTime(data.windowStart)}</Typography>
          ) : (
            <div className="label-flex-wrapper">
              <Typography>{`${formatValue(data.level)}${
                data?.uom ? ` ${data.uom}` : ""
              }`}</Typography>
            </div>
          )}
        </div>
        <div
          className={clsx(
            "baseWidth",
            !showFreeUserWindow && !showColumns.includes("Capacidade Total") && classes.hidden
          )}
        >
          {showFreeUserWindow ? (
            <Typography>{formatDateTime(data.windowEnd)}</Typography>
          ) : (
            <Typography>{`${formatValue(data.capacity)}${
              data?.uom ? ` ${data.uom}` : ""
            }`}</Typography>
          )}
        </div>

        <div
          className={clsx(
            "baseWidth",
            (showFreeUserWindow || !showColumns.includes("Autonomia Estimada")) && classes.hidden
          )}
        >
          <Typography>{`${autonomyOutage == 360 ? ">" : ""}${autonomyOutage} h`}</Typography>
        </div>
        {authProvider.hasHighLevel() && pressure && !showFreeUserWindow ? (
          <div className={clsx("baseWidth", !showColumns.includes("Pressão") && classes.hidden)}>
            <Typography>{pressure}</Typography>
          </div>
        ) : null}
        <div
          className={clsx(
            "baseWidth",
            !isTank && "bold",
            (showFreeUserWindow || !showColumns.includes("Status Nível")) && classes.hidden
          )}
          style={{ color: getLevelColor(data.status || data.level, data.thresholds) }}
        >
          <Typography>
            {getLevelStatusString(data.status || data.level, data.thresholds)}
          </Typography>
        </div>
        <div
          className={clsx(
            "date-wrapper",
            (showFreeUserWindow || !showColumns.includes("Última Medição")) && classes.hidden
          )}
        >
          <Typography color={delay ? "error" : "inherit"}>
            {isTank
              ? getDateFromTimezonePtBrToGMT(data.lastReading)
              : formatDateTime(data.lastReading)}
          </Typography>
        </div>
        <div className={clsx(classes.icon, !showPlannedIcon && classes.hidden)}>
          {data?.plannedTrip && (
            <Tooltip title={showPlannedIcon && "A próxima viagem de abastecimento está prevista"}>
              <span>
                <TruckIcon scale={1.2} />
              </span>
            </Tooltip>
          )}
        </div>
        <div className={clsx(classes.icon, !showTravelIcon && classes.hidden)}>
          {data?.tripInProgress && (
            <Tooltip title={showTravelIcon && "A viagem de abastecimento está em rota"}>
              <span>
                <TruckIcon altVersion color="primary" scale={1.2} />
              </span>
            </Tooltip>
          )}
        </div>
        <div className={clsx(!showTanks && classes.hidden)}>
          <Typography>{data?.numberOfThanks}</Typography>
          {tanksWarnings && (
            <Tooltip
              onOpen={() => showTanks && setNowDate(new Date())}
              title={showTanks && tanksWarnings}
            >
              <ErrorIcon color="error" fontSize="small" />
            </Tooltip>
          )}
        </div>
        {showAdditionalWindowColumns && (
          <>
            <div className="baseWidth">
              <Typography>{formatDateTime(data.windowStart)}</Typography>
            </div>
            <div className="baseWidth">
              <Typography>{formatDateTime(data.windowEnd)}</Typography>
            </div>
          </>
        )}
        <div className={clsx("last", "disable-icon-padding")}>
          {data.Tanks && !isFreeUserRow && (
            <Tooltip
              title={
                isSingleTank
                  ? ""
                  : isExpanded
                  ? "clique para colapsar a linha"
                  : "clique para expandir a linha e exibir os detalhes dos tanques"
              }
            >
              <span>
                <IconButton disabled={isSingleTank}>
                  {!isExpanded ? <ExpandMoreIcon /> : <ExpandLessIcon />}
                </IconButton>
              </span>
            </Tooltip>
          )}
        </div>
      </div>
    );
  }
  const classes = useStyles();

  if (
    !isFreeUserRow &&
    isArrayValid(data.Tanks) &&
    data?.Tanks?.length &&
    data?.Tanks?.length > 1
  ) {
    return (
      <Accordion
        elevation={0}
        expanded={isExpanded}
        onChange={() => setExpanded((prev) => !prev)}
        key={data.id}
        className={classes.accordion}
        TransitionProps={{ unmountOnExit: true }}
      >
        <AccordionSummary expandIcon={null}>{HomeTableRowMain()}</AccordionSummary>

        <AccordionDetails>
          <div className={classes.tanksWrapper}>
            {data?.Tanks?.map((tank) => (
              <HomeTableRow
                onSeeAsMode={onSeeAsMode}
                key={tank.tank_id}
                data={{
                  id: tank.tank_id,
                  level: tank.tank_level_su,
                  capacity: tank.tank_capacity_su,
                  uom: tank.product_uom,
                  thresholds: {
                    veryLow: tank.outage_level,
                    over: tank.tank_capacity_su,
                  },
                  lastReading: tank?.last_rtu_reading_date
                    ? new Date(tank.last_rtu_reading_date)
                    : undefined,
                  status: tank.tank_level_status,
                  autonomy: data.autonomy,
                  tank_pressure: tank.tank_pressure,
                  tank_pressure_uom: tank.tank_pressure_uom,
                }}
                isTank
                hasFavorites={hasFavorites}
              />
            ))}
          </div>
        </AccordionDetails>
      </Accordion>
    );
  }

  return HomeTableRowMain();
}

type HomeTable = {
  installations?: InstallationModel[];
  customerId?: string;
  expanded: boolean;
  disableClick?: boolean;
  favoriteGroup?: boolean;
  hasFavorites?: boolean;
  onChange: () => void;
  onChangeOrder?: (
    customerId: string | undefined,
    idx: any,
    isReverse: boolean | undefined
  ) => void;
  handleFavorite?: (id: string, favoriteOption: boolean) => void;
  orderOptions?: OrderOptions;
  onLandingPage?: boolean;
};

function HomeTable({
  installations,
  customerId,
  expanded,
  disableClick = false,
  favoriteGroup,
  hasFavorites,
  onChange,
  onChangeOrder,
  orderOptions,
  onLandingPage,
  handleFavorite,
}: HomeTable) {
  const classes = useStyles();
  const authProvider = useAuth();
  const theme = useTheme();
  const isMdDown = useMediaQuery(theme.breakpoints.down("md"));
  const showColumns = useHubXValue(
    EVENT_HOME_PAGE_TABLE_COLUMNS_SELECTION_CHANGE,
    HomeTableColumns
  );

  const [onSeeAsMode, setOnSeeAsMode] = React.useState(false);

  const showFreeUserWindow = authProvider.isFreeUser;
  const showAdditionalWindowColumns = !authProvider.isFreeUser && favoriteGroup;

  useConstructor(() => {
    const sessionCache = new SessionCache();
    const user: IUserResponse | null = sessionCache.get("user");
    setOnSeeAsMode(user?.onSeeAsMode ?? false);
  });

  const ascending = (a: InstallationModel, b: InstallationModel, ref: any): any => {
    switch (ref) {
      case "product_code":
        return a.product_code && b.product_code && (a.product_code > b.product_code ? 1 : -1);
      case "name":
        return a.name && b.name && (a.name > b.name ? 1 : -1);
      case "installation_id":
        return a.installation_id > b.installation_id ? 1 : -1;
      case "tank_level_su":
        return calculateLevelPercentage(a.installation_level, a.inventory_capacity_su) >
          calculateLevelPercentage(b.installation_level, b.inventory_capacity_su)
          ? 1
          : -1;
      case "installation_level":
        return (
          a.installation_level &&
          b.installation_level &&
          (a.installation_level > b.installation_level ? 1 : -1)
        );
      case "autonomy_hours_to_outage":
        return (
          a.autonomy_hours_to_outage &&
          b.autonomy_hours_to_outage &&
          (a.autonomy_hours_to_outage > b.autonomy_hours_to_outage ? 1 : -1)
        );
      case "tank_pressure":
        return a.tanks &&
          a.tanks?.length == 1 &&
          b?.tanks &&
          b?.tanks?.length == 1 &&
          a?.tanks[0] &&
          b?.tanks[0] &&
          a?.tanks[0].tank_pressure &&
          b?.tanks[0].tank_pressure
          ? a?.tanks[0].tank_pressure > b?.tanks[0].tank_pressure
            ? 1
            : -1
          : -1;
      case "inventory_capacity_su":
        return (
          a.inventory_capacity_su &&
          b.inventory_capacity_su &&
          (a.inventory_capacity_su > b.inventory_capacity_su ? 1 : -1)
        );
      case "installation_level_status":
        return (
          a.installation_level_status &&
          b.installation_level_status &&
          (a.installation_level_status > b.installation_level_status ? 1 : -1)
        );
      case "last_rtu_reading_date":
        return (
          a.last_rtu_reading_date &&
          b.last_rtu_reading_date &&
          (a.last_rtu_reading_date > b.last_rtu_reading_date ? 1 : -1)
        );
      case "number_of_tanks":
        return (
          a.number_of_tanks && b.number_of_tanks && (a.number_of_tanks > b.number_of_tanks ? 1 : -1)
        );
      case "tripInProgress": {
        const aTrip = checkTripInProgress(a.trip_status) ? -1 : 1;
        const bTrip = checkTripInProgress(b.trip_status) ? -1 : 1;
        return aTrip === bTrip ? 0 : aTrip > bTrip ? 1 : -1;
      }
      case "plannedTrip": {
        const aTrip = checkTripPlanned(a.trip_status) ? -1 : 1;
        const bTrip = checkTripPlanned(b.trip_status) ? -1 : 1;
        return aTrip === bTrip ? 0 : aTrip > bTrip ? 1 : -1;
      }
      default:
        return 1;
        break;
    }
  };

  const descending = (a: InstallationModel, b: InstallationModel, ref: any): any => {
    switch (ref) {
      case "product_code":
        return a.product_code && b.product_code && (a.product_code > b.product_code ? -1 : 1);
      case "name":
        return a.name && b.name && (a.name > b.name ? -1 : 1);
      case "installation_id":
        return a.installation_id > b.installation_id ? -1 : 1;
      case "tank_level_su":
        return calculateLevelPercentage(a.installation_level, a.inventory_capacity_su) >
          calculateLevelPercentage(b.installation_level, b.inventory_capacity_su)
          ? -1
          : 1;
      case "installation_level":
        return (
          a.installation_level &&
          b.installation_level &&
          (a.installation_level > b.installation_level ? -1 : 1)
        );

      case "autonomy_hours_to_outage":
        return (
          a.autonomy_hours_to_outage &&
          b.autonomy_hours_to_outage &&
          (a.autonomy_hours_to_outage > b.autonomy_hours_to_outage ? -1 : 1)
        );
      case "tank_pressure":
        return a.tanks &&
          a.tanks?.length == 1 &&
          b?.tanks &&
          b?.tanks?.length == 1 &&
          a?.tanks[0] &&
          b?.tanks[0] &&
          a?.tanks[0].tank_pressure &&
          b?.tanks[0].tank_pressure
          ? a?.tanks[0].tank_pressure > b?.tanks[0].tank_pressure
            ? -1
            : 1
          : 1;
      case "inventory_capacity_su":
        return (
          a.inventory_capacity_su &&
          b.inventory_capacity_su &&
          (a.inventory_capacity_su > b.inventory_capacity_su ? -1 : 1)
        );
      case "installation_level_status":
        return (
          a.installation_level_status &&
          b.installation_level_status &&
          (a.installation_level_status > b.installation_level_status ? -1 : 1)
        );
      case "last_rtu_reading_date":
        return (
          a.last_rtu_reading_date &&
          b.last_rtu_reading_date &&
          (a.last_rtu_reading_date > b.last_rtu_reading_date ? -1 : 1)
        );
      case "number_of_tanks":
        return (
          a.number_of_tanks && b.number_of_tanks && (a.number_of_tanks > b.number_of_tanks ? -1 : 1)
        );
      case "tripInProgress": {
        const aTrip = checkTripInProgress(a.trip_status) ? -1 : 1;
        const bTrip = checkTripInProgress(b.trip_status) ? -1 : 1;
        return aTrip === bTrip ? 0 : aTrip > bTrip ? -1 : 1;
      }
      case "plannedTrip": {
        const aTrip = checkTripPlanned(a.trip_status) ? -1 : 1;
        const bTrip = checkTripPlanned(b.trip_status) ? -1 : 1;
        return aTrip === bTrip ? 0 : aTrip > bTrip ? -1 : 1;
      }
      default:
        return 1;
        break;
    }
  };

  const [columnClicked, setColumnClicked] = React.useState<string | null>(null);
  const [isReverse, setIsReverse] = React.useState<boolean | undefined>(false);

  const UseOrderColumn = (column: string) => {
    if (onLandingPage) {
      if (columnClicked) {
        if (columnClicked === column) {
          return isReverse ? (
            <ArrowDropDownIcon color="primary" />
          ) : (
            <ArrowDropUpIcon color="primary" />
          );
        }
      }
    } else {
      if (orderOptions?.column != null) {
        if (orderOptions?.column == column) {
          return orderOptions?.order ? (
            <ArrowDropDownIcon color="primary" />
          ) : (
            <ArrowDropUpIcon color="primary" />
          );
        }
      }
    }
    return null;
  };

  const [installationFiltered, setInstallationFiltered] = React.useState<
    IInstallation[] | undefined
  >();

  const onclick = async (value: string) => {
    const hasIsReverse = orderOptions?.order || isReverse;
    let sortInstalations = false;
    if (value == columnClicked || orderOptions?.column == value) {
      if (orderOptions?.column) {
        setColumnClicked(orderOptions?.column);
      }

      setIsReverse(!hasIsReverse);
      sortInstalations = !hasIsReverse;
    } else {
      setColumnClicked(value);
      setIsReverse(false);
    }

    if (value != null) {
      if (onLandingPage || favoriteGroup) {
        setInstallationFiltered(
          installationFiltered?.sort((a: IInstallation, b: IInstallation) =>
            sort(sortInstalations, a, b, value)
          )
        );
      } else {
        const id = favoriteGroup ? "favorite" : customerId;
        onChangeOrder && (await onChangeOrder(id, value, !hasIsReverse));
      }
    }
  };

  const sort = (sortInstalations: boolean, a: any, b: any, ref: any) => {
    return sortInstalations ? descending(a, b, ref) : ascending(a, b, ref);
  };

  React.useMemo(() => {
    setInstallationFiltered(installations);
    setColumnClicked(null);
    setIsReverse(false);
  }, [installations]);

  const renderHeader = () => {
    return (
      <div
        className={
          hasFavorites
            ? clsx(classes.gridRow, classes.gridRowFavorites, "header")
            : clsx(classes.gridRow, "header")
        }
        style={
          showAdditionalWindowColumns
            ? {
                gridTemplateColumns: isMdDown
                  ? FAVORITES_WINDOW_COLUMNS_MD
                  : FAVORITES_WINDOW_COLUMNS,
                minWidth: isMdDown ? FAVORITES_WINDOW_MIN_WIDTH_MD : FAVORITES_WINDOW_MIN_WIDTH,
              }
            : undefined
        }
      >
        {hasFavorites && <div>{favoriteGroup ? <StarIcon /> : <StarBorderIcon />}</div>}
        <div
          onClick={() => !disableClick && onclick("product_code")}
          className={clsx("baseWidth", onLandingPage && "divPointer")}
        >
          <Typography>Produto</Typography>
          {UseOrderColumn("product_code")}
        </div>
        <div
          onClick={() => !disableClick && onclick("name")}
          className={clsx(
            !showColumns.includes("Nome Fantasia") && classes.hidden,
            onLandingPage && "divPointer"
          )}
        >
          <Typography>Nome Fantasia</Typography>
          {UseOrderColumn("name")}
        </div>
        <div
          onClick={() => !disableClick && onclick("installation_id")}
          className={clsx(onLandingPage && "divPointer")}>
          <Typography>ID</Typography>
          {UseOrderColumn("installation_id")}
        </div>
        <div
          onClick={() => onLandingPage && !disableClick && onclick("tank_level_su")}
          className={clsx(
            (showFreeUserWindow || !showColumns.includes("Nível")) && classes.hidden,
            onLandingPage && "divPointer"
          )}
        >
          <Typography>Nível</Typography>
          {UseOrderColumn("tank_level_su")}
        </div>
        <div
          onClick={() => !showFreeUserWindow && !disableClick && onclick("installation_level")}
          className={clsx(
            "baseWidth",
            !showFreeUserWindow && !showColumns.includes("Nível Instântaneo") && classes.hidden,
            onLandingPage && !showFreeUserWindow && "divPointer"
          )}
        >
          <Typography>{showFreeUserWindow ? "Prev. de Entrega" : "Nível Instântaneo"}</Typography>
          {!showFreeUserWindow && UseOrderColumn("installation_level")}
        </div>
        <div
          onClick={() => !showFreeUserWindow && !disableClick && onclick("inventory_capacity_su")}
          className={clsx(
            "baseWidth",
            !showFreeUserWindow && !showColumns.includes("Capacidade Total") && classes.hidden,
            onLandingPage && !showFreeUserWindow && "divPointer"
          )}
        >
          <Typography>{showFreeUserWindow ? "Prev. de Entrega Máx." : "Capacidade Total"}</Typography>
          {!showFreeUserWindow && UseOrderColumn("inventory_capacity_su")}
        </div>

        <div
          onClick={() => !disableClick && onclick("autonomy_hours_to_outage")}
          className={clsx(
            "baseWidth",
            (showFreeUserWindow || !showColumns.includes("Autonomia Estimada")) && classes.hidden,
            onLandingPage && "divPointer"
          )}
        >
          <Typography>Autonomia Estimada</Typography>
          {UseOrderColumn("autonomy_hours_to_outage")}
        </div>
        {authProvider.hasHighLevel() && !showFreeUserWindow ? (
          <div
            onClick={() => !disableClick && onclick("tank_pressure")}
            className={clsx(
              "baseWidth",
              !showColumns.includes("Pressão") && classes.hidden,
              onLandingPage && "divPointer"
            )}
          >
            <Typography>Pressão</Typography>
            {UseOrderColumn("tank_pressure")}
          </div>
        ) : null}
        <div
          onClick={() => !disableClick && onclick("installation_level_status")}
          className={clsx(
            "baseWidth",
            (showFreeUserWindow || !showColumns.includes("Status Nível")) && classes.hidden,
            onLandingPage && "divPointer"
          )}
        >
          <Typography>Status Nível</Typography>
          {UseOrderColumn("installation_level_status")}
        </div>
        <div
          onClick={() => !disableClick && onclick("last_rtu_reading_date")}
          className={clsx(
            (showFreeUserWindow || !showColumns.includes("Última Medição")) && classes.hidden,
            onLandingPage && "divPointer"
          )}
        >
          <Typography>Última Medição</Typography>
          {UseOrderColumn("last_rtu_reading_date")}
        </div>
        <div
          onClick={() => onLandingPage && !disableClick && onclick("plannedTrip")}
          className={clsx(
            !showColumns.includes("Viagem Planejada") && classes.hidden,
            onLandingPage && "divPointer"
          )}
        >
          <Typography>Viagem Planejada</Typography>
          {UseOrderColumn("plannedTrip")}
        </div>
        <div
          onClick={() => onLandingPage && !disableClick && onclick("tripInProgress")}
          className={clsx(
            !showColumns.includes("Viagem Em Rota") && classes.hidden,
            onLandingPage && "divPointer"
          )}
        >
          <Typography>Viagem Em Rota</Typography>
          {UseOrderColumn("tripInProgress")}
        </div>
        <div
          onClick={() => !disableClick && onclick("number_of_tanks")}
          className={clsx(
            (showFreeUserWindow || !showColumns.includes("Tanques")) && classes.hidden,
            onLandingPage && "divPointer"
          )}
        >
          <Typography>Tanques</Typography>
          {UseOrderColumn("number_of_tanks")}
        </div>
        {showAdditionalWindowColumns && (
          <>
            <div className="baseWidth">
              <Typography>Prev. de Entrega</Typography>
            </div>
            <div className="baseWidth">
              <Typography>Prev. de Entrega Máx.</Typography>
            </div>
          </>
        )}
        {/* Placeholder for expand icon */}
        <div />
      </div>
    );
  };

  return (
    <div className={classes.root}>
      {/* Table Header */}
      {renderHeader()}
      {/* Table installation Rows */}
      {columnClicked != undefined
        ? installationFiltered?.map((installation) => (
            <HomeTableRow
              key={installation.installation_id + customerId}
              favoriteGroup={favoriteGroup}
              hasFavorites={hasFavorites}
              onSeeAsMode={onSeeAsMode}
              data={{
                id: installation.installation_id,
                name: installation.name,
                linkLabel: installation.product_code,
                link: `/installation/${customerId}/${installation.installation_id}`,
                level: installation.installation_level,
                uom: installation.product_uom,
                capacity: installation.inventory_capacity_su,
                thresholds: {
                  veryLow: installation.outage_level_su,
                  low: installation.reserve_level_su,
                  over: installation.inventory_capacity_su,
                },
                lastReading: installation.last_rtu_reading_date
                  ? new Date(installation.last_rtu_reading_date)
                  : undefined,
                plannedTrip: checkTripPlanned(installation.trip_status),
                tripInProgress: checkTripInProgress(installation.trip_status),
                numberOfThanks: installation.number_of_tanks,
                Tanks: installation.tanks,
                status: installation.installation_level_status,
                favorite: installation.favorite,
                vendor: installation.rtu_vendor,
                autonomy: installation.autonomy_hours_to_outage,
                windowStart: installation.winow_start,
                windowEnd: installation.window_end,
              }}
            />
          ))
        : installationFiltered?.map((installation) => (
            <HomeTableRow
              key={installation.installation_id}
              favoriteGroup={favoriteGroup}
              hasFavorites={hasFavorites}
              handleFavorite={handleFavorite}
              onSeeAsMode={onSeeAsMode}
              data={{
                id: installation.installation_id,
                name: installation.name,
                linkLabel: installation.product_code,
                link: `/installation/${customerId}/${installation.installation_id}`,
                level: installation.installation_level,
                uom: installation.product_uom,
                capacity: installation.inventory_capacity_su,
                thresholds: {
                  veryLow: installation.outage_level_su,
                  low: installation.reserve_level_su,
                  over: installation.inventory_capacity_su,
                },
                lastReading: installation.last_rtu_reading_date
                  ? new Date(installation.last_rtu_reading_date)
                  : undefined,
                plannedTrip: checkTripPlanned(installation.trip_status),
                tripInProgress: checkTripInProgress(installation.trip_status),
                numberOfThanks: installation.number_of_tanks,
                Tanks: installation.tanks,
                status: installation.installation_level_status,
                favorite: installation.favorite,
                vendor: installation.rtu_vendor,
                autonomy: installation.autonomy_hours_to_outage,
                windowStart: installation.winow_start,
                windowEnd: installation.window_end,
              }}
            />
          ))}
    </div>
  );
}

export default HomeTable;
